import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Grid, Text, Spinner, Flex, Icon, Button, VStack, Skeleton, SkeletonText } from "@chakra-ui/react";
import { fetchLikeProperty } from "../../api/Property/propertyApi";
import { PropertyCard } from "./Card";
import { FormattedMessage } from "react-intl";
import { FaHeart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function SavedProperties({ refetchSavedProperties }) {
  const [dislikeTrigger, setDislikeTrigger] = useState(false);
  const [localFavorites, setLocalFavorites] = useState([]);
  const auth = JSON.parse(localStorage.getItem("userData"));
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: favorite, isLoading, refetch } = useQuery({
    enabled: !!auth?.access_token,
    queryKey: ["PropertyLikes"],
    queryFn: () => fetchLikeProperty(auth?.id),
    select: (res) => res?.data?.records,
  });

  useEffect(() => {
    if (favorite) {
      setLocalFavorites(favorite);
    }
  }, [favorite]);

  const handleDislikeSuccess = (itemId) => {
    setLocalFavorites(prev => prev.filter(f => f.id !== itemId));
    queryClient.invalidateQueries(["PropertyLikes"]);
  }

  useEffect(() => {
    if (dislikeTrigger) {
      refetch();
      setDislikeTrigger(false);
    }
  }, [dislikeTrigger, refetch]);

  return (
    <Box p={{ base: 2, md: 0 }}>
      <Flex
        justify="space-between"
        align="center"
        mb={8}
        flexDirection={{ base: "column", md: "row" }}
        gap={4}
      >
        <Box>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color="gray.800"
            lineHeight="shorter"
          >
            <FormattedMessage id="savedProperties.title" defaultMessage="Favorite Properties" />
          </Text>
          <Text color="gray.500" fontSize="sm" mt={1}>
            {localFavorites?.length || 0} <FormattedMessage id="savedProperties.count" defaultMessage="properties found" />
          </Text>
        </Box>
      </Flex>

      {isLoading ? (
        <Grid templateColumns="1fr" gap={6} mt={4}>
          {[1, 2, 3].map((i) => (
            <Box
              key={i}
              p={4}
              borderWidth="1px"
              borderRadius="2xl"
              bg="white"
              borderColor="gray.100"
            >
              <Grid templateColumns={{ base: "1fr", md: "1.5fr 2fr" }} gap={6}>
                <Skeleton height={{ base: "170px", md: "250px" }} borderRadius="xl" />
                <VStack align="start" spacing={4} pt={2}>
                  <Skeleton height="15px" width="100px" />
                  <Skeleton height="25px" width="150px" />
                  <Skeleton height="20px" width="80%" />
                  <Skeleton height="20px" width="60%" />
                  <Flex gap={4} w="100%" pt={4}>
                    <Skeleton height="35px" flex="1" borderRadius="md" />
                    <Skeleton height="35px" flex="1" borderRadius="md" />
                    <Skeleton height="35px" flex="1" borderRadius="md" />
                  </Flex>
                </VStack>
              </Grid>
            </Box>
          ))}
        </Grid>
      ) : (
        <Grid
          templateColumns="1fr"
          gap={6}
          mt={4}
        >
          {localFavorites?.map((item) => (
            <PropertyCard
              key={item.id}
              item={item.Property}
              liked={[{ id: item.id }]}
              refetch={refetch}
              refetchSavedProperties={refetchSavedProperties}
              handleDislikeSuccess={handleDislikeSuccess}
              setDislikeTrigger={setDislikeTrigger}
              isSavedProperties={true}
            />
          ))}
        </Grid>
      )}

      {localFavorites?.length === 0 && !isLoading && (
        <Flex
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          textAlign="center"
          bg="gray.50"
          borderRadius="2xl"
          p={10}
        >
          <Box
            bg="white"
            p={6}
            borderRadius="full"
            boxShadow="0 10px 25px rgba(0,0,0,0.05)"
            mb={6}
          >
            <Icon as={FaHeart} fontSize="4xl" color="gray.200" />
          </Box>
          <VStack spacing={2}>
            <Text fontSize="xl" fontWeight="bold" color="gray.700">
              <FormattedMessage id="savedProperties.noSaved" defaultMessage="Your wish list is empty" />
            </Text>
            <Text color="gray.500" maxW="300px">
              <FormattedMessage id="savedProperties.noSavedDesc" defaultMessage="Save your favorite properties to review them later and keep track of your dream home." />
            </Text>
          </VStack>
          <Button
            mt={8}
            colorScheme="red"
            size="lg"
            px={8}
            onClick={() => navigate("/property-listings")}
            borderRadius="full"
            boxShadow="0 4px 14px 0 rgba(229, 62, 62, 0.39)"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(229, 62, 62, 0.23)",
            }}
          >
            <FormattedMessage id="savedProperties.explore" defaultMessage="Explore Properties" />
          </Button>
        </Flex>
      )}
    </Box>
  );
}

export default SavedProperties;
